import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Camera, Upload, X, Loader2, RotateCcw, Check, Settings2 } from "lucide-react";
import { StripBrands } from "./StripBrands";

const toJpeg = (src) => new Promise((resolve) => {
  const scale = Math.min(1, 1600 / Math.max(src.width || src.videoWidth, src.height || src.videoHeight));
  const c = document.createElement("canvas");
  c.width = Math.round((src.width || src.videoWidth) * scale); c.height = Math.round((src.height || src.videoHeight) * scale);
  c.getContext("2d").drawImage(src, 0, 0, c.width, c.height);
  resolve(c.toDataURL("image/jpeg", 0.85));
});
const fileToJpeg = (file) => new Promise((resolve, reject) => {
  const img = new Image(); img.onload = () => toJpeg(img).then(resolve); img.onerror = reject; img.src = URL.createObjectURL(file);
});

function LiveCamera({ onShot, onCancel }) {
  const vid = useRef(null);
  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 } } })
      .then((s) => { stream = s; if (vid.current) vid.current.srcObject = s; })
      .catch(() => { toast.error("Camera not available — use Upload / Phone camera instead."); onCancel(); });
    return () => stream && stream.getTracks().forEach((t) => t.stop());
  }, [onCancel]);
  return (
    <div className="space-y-2" data-testid="strip-live-camera">
      <video ref={vid} autoPlay playsInline muted className="w-full rounded-md bg-black max-h-[50vh] object-contain" />
      <div className="flex gap-2 justify-center">
        <button onClick={() => toJpeg(vid.current).then(onShot)} data-testid="strip-take-photo" className="px-5 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[13px] flex items-center gap-1.5"><Camera className="h-4 w-4" /> Take Photo</button>
        <button onClick={onCancel} className="px-4 py-2 rounded-sm border border-slate-300 text-slate-600 text-[13px] font-bold hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
}

function PadRow({ pad, k, val, ai, onPick, onType }) {
  const near = val === "" || val == null ? null : pad.swatches.reduce((b, s) => (Math.abs(s[0] - val) < Math.abs(b[0] - val) ? s : b), pad.swatches[0])[0];
  return (
    <div className="grid grid-cols-[11.5rem_1fr_4.5rem] items-center gap-2 py-1 border-b border-slate-100" data-testid={`strip-pad-${k}`}>
      <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#123a63]">
        {ai?.hex && <span className="h-4 w-4 rounded-sm border border-slate-300 shrink-0" style={{ background: ai.hex }} title="Colour seen in photo" />}
        <span className="truncate">{pad.label}</span>
        {ai?.confidence === "low" && <span className="text-[9px] font-black text-amber-700 bg-amber-100 px-1 rounded">CHECK</span>}
      </div>
      <div className="flex gap-1">
        {pad.swatches.map(([v, c]) => (
          <button key={v} onClick={() => onPick(v)} data-testid={`strip-swatch-${k}-${v}`} title={String(v)}
            className={`flex-1 min-w-0 rounded-sm border-2 text-[10px] font-black py-1.5 transition-transform hover:-translate-y-0.5 ${near === v ? "border-[#123a63] ring-2 ring-sky-300" : "border-white"}`}
            style={{ background: c, color: "#1a2b3c" }}>{v}</button>
        ))}
      </div>
      <input value={val ?? ""} onChange={(e) => onType(e.target.value)} inputMode="decimal" data-testid={`strip-val-${k}`}
        className="bg-[#cfeaf5] border border-[#7ba7d9] rounded-sm px-1 py-1 text-[13px] font-bold text-center w-full outline-none focus:bg-white" />
    </div>
  );
}

export function StripReader({ onApply, onClose }) {
  const [charts, setCharts] = useState(null);
  const [type, setTypeRaw] = useState(() => localStorage.getItem("aqx:strip-brand") || "");
  const setType = (t) => { setTypeRaw(t); localStorage.setItem("aqx:strip-brand", t); };
  const [manage, setManage] = useState(false);
  const [mode, setMode] = useState("pick"); // pick | camera | reading | result
  const [ai, setAi] = useState({});
  const [vals, setVals] = useState({});
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const load = () => api("GET", "/test-strips").then(setCharts).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const brand = charts?.brands.find((b) => b.id === type);
  const known = charts && (brand || (!charts.brands.length && charts.types[type]));
  useEffect(() => { if (charts && !known) setTypeRaw(charts.default_id || "6in1"); }, [charts, known]); // no pick yet, or saved brand was removed
  const pads = !known ? [] : brand ? brand.pads.map((p) => p.key) : charts.types[type].pads;
  const padDef = (k) => (brand ? { label: charts.pads[k].label, swatches: brand.pads.find((p) => p.key === k).swatches } : charts.pads[k]);

  const read = async (dataUrl) => {
    setMode("reading");
    try {
      const r = await api("POST", "/test-strips/read", { ...(brand ? { brand_id: type } : { strip_type: type }), image_base64: dataUrl });
      if (!r.strip_found || !Object.keys(r.readings).length) { toast.error(r.notes || "Couldn't find a test strip in that photo — try again closer, in good light."); setMode("pick"); return; }
      setAi(r.readings); setVals(Object.fromEntries(Object.entries(r.readings).map(([k, v]) => [k, v.value])));
      setNote(r.notes || (r.chart_visible ? "" : "Tip: save this brand's colour chart (Brands…) for the most accurate reading."));
      setMode("result");
    } catch (e) { toast.error(e.message); setMode("pick"); }
  };
  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) read(await fileToJpeg(f)); };
  const apply = () => {
    const out = {}; pads.forEach((k) => { if (vals[k] !== "" && vals[k] != null && Number.isFinite(Number(vals[k]))) out[k === "total_hardness" ? "calcium_hardness" : k] = String(vals[k]); });
    onApply(out); toast.success("Test strip readings loaded — saved automatically");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-3" data-testid="strip-reader-modal">
      <div className="w-full max-w-2xl bg-gradient-to-b from-white to-[#eaf3fb] border border-[#7ba7d9] rounded-xl shadow-2xl max-h-[94vh] overflow-y-auto">
        <div className="flex justify-between items-center px-4 py-2.5 border-b border-[#7ba7d9] bg-[#dbe9f7] rounded-t-xl">
          <span className="font-black text-[#123a63] flex items-center gap-2"><Camera className="h-4 w-4" /> Read Test Strip</span>
          <button onClick={onClose} data-testid="strip-close" className="rounded-full p-1 hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-black text-[#123a63]">Strip brand</span>
            <select value={type} onChange={(e) => { setType(e.target.value); if (mode === "result") setMode("pick"); }} disabled={mode === "reading" || !charts} data-testid="strip-brand-select"
              className="flex-1 border border-[#7ba7d9] rounded-sm px-2 py-1 text-[13px] font-bold text-[#123a63] bg-white outline-none">
              {(charts?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              {!(charts?.brands || []).length && Object.entries(charts?.types || {}).map(([k, t]) => <option key={k} value={k}>{t.name} (standard chart)</option>)}
            </select>
            <button onClick={() => setManage(true)} disabled={!charts} data-testid="strip-brands-manage" className="px-2.5 py-1 rounded-sm border border-[#7ba7d9] text-[12px] font-black text-[#123a63] bg-white hover:bg-[#dbe9f7] flex items-center gap-1"><Settings2 className="h-3.5 w-3.5" />Brands…</button>
          </div>
          {manage && <StripBrands brands={charts.brands} pads={charts.pads} onClose={() => setManage(false)} onChanged={(b) => { load(); if (b?.id) setType(b.id); }} />}
          {mode === "pick" && (
            <div className="space-y-2" data-testid="strip-pick">
              <p className="text-[12px] text-slate-600">{brand
                ? <>Dip the strip, shake once, then photograph <b>just the strip on a white background</b> in good light within 60 seconds. It's compared to the saved {brand.name} chart.</>
                : <>Dip the strip, shake once, then photograph it <b>beside the bottle's colour chart</b> in good light within 60 seconds.</>}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <button onClick={() => setMode("camera")} data-testid="strip-use-camera" className="py-3 rounded-md bg-[#0e7490] hover:bg-[#0b5e75] text-white font-black text-[13px] flex items-center justify-center gap-2"><Camera className="h-4 w-4" /> Use Camera</button>
                <button onClick={() => fileRef.current?.click()} data-testid="strip-upload" className="py-3 rounded-md bg-white border-2 border-[#7ba7d9] hover:bg-[#dbe9f7] text-[#123a63] font-black text-[13px] flex items-center justify-center gap-2"><Upload className="h-4 w-4" /> Upload / Phone Camera</button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="strip-file-input" />
            </div>
          )}
          {mode === "camera" && <LiveCamera onShot={read} onCancel={() => setMode("pick")} />}
          {mode === "reading" && <div className="py-10 flex flex-col items-center gap-2 text-[#123a63]" data-testid="strip-reading"><Loader2 className="h-8 w-8 animate-spin" /><span className="text-[13px] font-bold">Reading the strip colours…</span></div>}
          {mode === "result" && (
            <div data-testid="strip-result">
              <p className="text-[12px] text-slate-600 mb-1">Check each pad — tap the matching colour if one is wrong, or type a value.</p>
              {pads.map((k) => <PadRow key={k} k={k} pad={padDef(k)} ai={ai[k]} val={vals[k]} onPick={(v) => setVals((s) => ({ ...s, [k]: v }))} onType={(v) => setVals((s) => ({ ...s, [k]: v }))} />)}
              {note && <p className="text-[11px] text-amber-700 mt-2" data-testid="strip-note">{note}</p>}
              <div className="flex justify-between mt-3">
                <button onClick={() => setMode("pick")} data-testid="strip-retake" className="px-4 py-1.5 rounded-sm border border-slate-300 text-slate-600 text-[13px] font-bold hover:bg-white flex items-center gap-1.5"><RotateCcw className="h-4 w-4" /> Retake</button>
                <button onClick={apply} data-testid="strip-apply" className="px-5 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[13px] flex items-center gap-1.5"><Check className="h-4 w-4" /> Use These Readings</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
